"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SocialNetworkFieldLogic = void 0;
const isPlainObject_1 = __importDefault(require("lodash-es/isPlainObject"));
const omit_1 = __importDefault(require("lodash-es/omit"));
const union_1 = __importDefault(require("lodash-es/union"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const types_1 = require("@smartsuite/types");
const validation_1 = require("../../validation/validation");
const field_logic_1 = require("../../field-type-registry/field-logic");
const fields_logic_registry_1 = require("../../field-type-registry/fields-logic-registry");
const comparisons_1 = require("../../comparisons/comparisons");
const fields_1 = require("../../utils/fields");
const social_network_fieldtype_1 = require("../../safe-field-value/text-fields/social-network-field/social-network.fieldtype");
const sort_1 = require("../../sort");
const clipboard_1 = require("../../clipboard/clipboard");
const object_1 = require("../../utils/object");
const intl_1 = require("../../locale/intl");
const texts_1 = require("../../texts");
const get_value_1 = require("../../safe-field-value/text-fields/social-network-field/get-value");
class SocialNetworkFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "socialnetworkfield";
        this.label = (0, intl_1.$t)(texts_1.socialNetworkFieldText.social_network);
        this.icon = "social-network";
        this.iconColor = "#FF9210";
        this.placeholder = (0, intl_1.$t)(texts_1.socialNetworkFieldText.specify_social_networks);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.socialNetworkFieldText.media),
            (0, intl_1.$t)(texts_1.socialNetworkFieldText.facebook),
            (0, intl_1.$t)(texts_1.socialNetworkFieldText.instagram),
            (0, intl_1.$t)(texts_1.socialNetworkFieldText.twitter),
            (0, intl_1.$t)(texts_1.socialNetworkFieldText.linkedin),
        ];
        this.groupByMode = "groupBy";
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is: comparisons_1.isListMultiple,
            is_not: comparisons_1.isNotListMultiple,
            is_empty: comparisons_1.isEmptyList,
            is_not_empty: comparisons_1.isNotEmptyList,
            contains: comparisons_1.contains,
            not_contains: comparisons_1.notContains,
        };
        this.fieldValueGetter = new social_network_fieldtype_1.SocialNetworkFieldValueGetter();
        this.clipboardPasteRules = {
            textfield: clipboard_1.clipboardTextToSocial,
            recordtitlefield: clipboard_1.clipboardTextToSocial,
            textareafield: clipboard_1.clipboardTextToSocial,
            richtextareafield: clipboard_1.clipboardTextToSocial,
            linkfield: clipboard_1.clipboardTextToSocial,
            socialnetworkfield: clipboard_1.clipboardSocialToSocial,
            unknownfield: clipboard_1.clipboardTextToSocial,
        };
        this.delimiter = ", ";
    }
    get validators() {
        return { required: validation_1.requiredSocialNetwork };
    }
    getInlineEditRules(field) {
        const isSingle = field.params.entries_allowed === "single";
        const baseInlineEditRules = super.getInlineEditRules(field);
        return {
            ...super.getInlineEditRules(field),
            switchToInputControl: "OnSelectedCellClick",
            editOnStartTyping: false,
            saveKeys: isSingle ? baseInlineEditRules.saveKeys : ["Enter"],
            editorWidth: isSingle ? "100%" : "150%",
            hideError: !isSingle,
        };
    }
    // @ts-expect-error: Need to resolve
    getComparisons(field, rule) {
        const [, nested] = (0, fields_1.resolveFieldBySlug)([field], rule.field);
        if (nested) {
            const nestedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(nested.field_type);
            return nestedFieldLogic.getComparisons(nested, rule);
        }
        return this.comparisons;
    }
    processFilterValue(filterValue, comparison) {
        if (comparison.includes("is_empty")) {
            return this.getEmptyValue();
        }
        return filterValue;
    }
    processFieldValue(record, slug, comparison, field) {
        var _a;
        const fieldValues = [];
        const isFilterComparisons = ["contains", "not_contains"].includes(comparison);
        const [, nested] = (0, fields_1.resolveFieldBySlug)([field], slug);
        const value = (0, fields_1.getValueFromRecordByField)(record, field);
        if (nested) {
            const nestedSlug = nested.slug;
            return (_a = value[nestedSlug]) !== null && _a !== void 0 ? _a : "";
        }
        for (const key of (0, object_1.getKeys)(value)) {
            const element = value[key];
            if (key === "sys_root" || !element) {
                continue;
            }
            const companySocial = this.getSocialNetworkType(key);
            if (isFilterComparisons) {
                // prepare full url for a search, i.e. https://www.facebook.com/fb
                const fullUrl = `${types_1.SocialNetworksURLs[companySocial]}/${element}`;
                fieldValues.push(fullUrl);
            }
            else {
                fieldValues.push(types_1.socialChoices[companySocial]);
            }
        }
        return isFilterComparisons ? fieldValues.join(", ") : fieldValues;
    }
    getSocialNetworkType(socialNetwork) {
        switch (socialNetwork) {
            case "facebook_username":
                return "facebook";
            case "twitter_username":
                return "twitter";
            case "linkedin_username":
                return "linkedin";
            case "instagram_username":
                return "instagram";
        }
    }
    getEmptyValue() {
        return (0, get_value_1.getSocialNetworkFieldEmptyValue)();
    }
    isValueEmpty(fieldValue) {
        if (!fieldValue || !(0, isPlainObject_1.default)(fieldValue)) {
            return true;
        }
        return (fieldValue.facebook_username === "" &&
            fieldValue.linkedin_username === "" &&
            fieldValue.instagram_username === "" &&
            fieldValue.twitter_username === "" &&
            (fieldValue.sys_root === undefined || fieldValue.sys_root === ""));
    }
    valueToString(fieldValue, field) {
        var _a, _b;
        if (Array.isArray(fieldValue)) {
            return fieldValue.filter((part) => !!part).join(this.delimiter);
        }
        if (typeof fieldValue === "string") {
            return fieldValue !== null && fieldValue !== void 0 ? fieldValue : "";
        }
        if (!fieldValue) {
            // when grouping we receive an undefined/null value so we try to rely
            // on the structure of the getEmptyValue to execute the filter/maps down the line
            fieldValue = this.getEmptyValue();
        }
        return ((_b = (_a = field.params.nested) === null || _a === void 0 ? void 0 : _a.filter(({ params }) => !params.hidden).map(({ slug }) => fieldValue[slug]).filter((part) => !!part).join(this.delimiter)) !== null && _b !== void 0 ? _b : "");
    }
    valueToExport(fieldValue, field) {
        if (Array.isArray(fieldValue)) {
            return fieldValue.join(",");
        }
        if (typeof fieldValue === "string") {
            return fieldValue !== null && fieldValue !== void 0 ? fieldValue : "";
        }
        return this.prepareValueForClipboard(fieldValue, field).text;
    }
    valueToGoogleSheet(fieldValue, field) {
        const value = this.prepareValueForClipboard(fieldValue, field);
        return {
            userEnteredValue: {
                stringValue: value.text,
            },
        };
    }
    filterValueToString(filterValue) {
        if (Array.isArray(filterValue)) {
            return filterValue.join(this.delimiter);
        }
        return filterValue;
    }
    fieldParamsFilterValueToDisplay(filterValue, _field, _userPreferences) {
        if (Array.isArray(filterValue.value)) {
            return filterValue.value
                .map((filterValueItem) => types_1.socialTypes[filterValueItem])
                .join(this.delimiter);
        }
        return types_1.socialTypes[Number(filterValue.value)] || filterValue.value;
    }
    sortKey(fieldValue, field, userPreferences, record, structure, rule) {
        const sortByField = (0, fields_1.getAppFieldBySlug)(structure, rule.field);
        if ((0, fields_1.isFieldNested)(sortByField)) {
            return (0, sort_1.getNestedFieldSortKey)(sortByField, structure, rule, userPreferences, record);
        }
        return this.valueToString(fieldValue, field);
    }
    groupByKey(fieldValue) {
        if (!fieldValue) {
            return "";
        }
        const fieldValues = [];
        const nestedFieldKeys = [
            "facebook_username",
            "twitter_username",
            "linkedin_username",
            "instagram_username",
        ];
        for (const key of nestedFieldKeys) {
            if (fieldValue[key]) {
                fieldValues.push(key);
            }
        }
        return fieldValues.join(",");
    }
    groupBySortKey(fieldValue, field, userPreferences, record, structure, rule) {
        const sortByField = (0, fields_1.getAppFieldBySlug)(structure, rule.field);
        if ((0, fields_1.isFieldNested)(sortByField)) {
            return (0, sort_1.getNestedFieldSortKey)(sortByField, structure, rule, userPreferences, record);
        }
        return this.groupByKey(fieldValue);
    }
    prepareValueForClipboard(fieldValue, _field) {
        const linkList = [];
        if (fieldValue.facebook_username) {
            linkList.push(types_1.socialNetworkInputProperties.facebook.template.replace("_value_", fieldValue.facebook_username));
        }
        if (fieldValue.instagram_username) {
            linkList.push(types_1.socialNetworkInputProperties.instagram.template.replace("_value_", fieldValue.instagram_username));
        }
        if (fieldValue.linkedin_username) {
            linkList.push(types_1.socialNetworkInputProperties.linkedin.template.replace("_value_", fieldValue.linkedin_username));
        }
        if (fieldValue.twitter_username) {
            linkList.push(types_1.socialNetworkInputProperties.twitter.template.replace("_value_", fieldValue.twitter_username));
        }
        return {
            text: linkList.toString(),
            rawValue: fieldValue,
        };
    }
    showRollback(field) {
        return field.params.entries_allowed === "single";
    }
    getFilterComponentData(field, comparison) {
        return {
            ...super.getFilterComponentData(field, comparison),
            placeholder: comparison === "contains"
                ? (0, intl_1.$t)(texts_1.socialNetworkFieldText.enter_text_here)
                : this.placeholder,
            choices: field.params.choices,
        };
    }
    formatActivityRecordChangeValues(previousValue, currentValue) {
        if (this.isValueEmpty(previousValue) || this.isValueEmpty(currentValue)) {
            return { previousValue, currentValue };
        }
        const formattedPreviousValue = {};
        const formattedCurrentValue = {};
        const properties = (0, union_1.default)((0, object_1.getKeys)(previousValue), (0, object_1.getKeys)(currentValue));
        properties.forEach((property) => {
            if (property === "sys_root") {
                return;
            }
            if (previousValue[property] !== currentValue[property]) {
                // @ts-expect-error: Need to resolve
                formattedPreviousValue[property] = previousValue[property];
                // @ts-expect-error: Need to resolve
                formattedCurrentValue[property] = currentValue[property];
            }
        });
        return {
            previousValue: formattedPreviousValue,
            currentValue: formattedCurrentValue,
        };
    }
    getGroupLabel(fieldValue, field) {
        if ((0, object_1.isObject)(fieldValue)) {
            return (0, object_1.getKeys)(fieldValue)
                .filter((key) => key !== "sys_root")
                .filter((key) => !!fieldValue[key])
                .map((key) => `${this.getSocialNetworkName(key)}: ${String(fieldValue[key])}`)
                .join(this.delimiter);
        }
        return this.valueToString(fieldValue, field);
    }
    getSocialNetworkName(key) {
        switch (key) {
            case "facebook_username":
                return types_1.socialNetworkNames.facebook;
            case "twitter_username":
                return types_1.socialNetworkNames.twitter;
            case "linkedin_username":
                return types_1.socialNetworkNames.linkedin;
            case "instagram_username":
                return types_1.socialNetworkNames.instagram;
        }
    }
    isValueEqual(fieldValue1, fieldValue2) {
        return (0, react_fast_compare_1.default)((0, omit_1.default)(fieldValue1, ["sys_root"]), (0, omit_1.default)(fieldValue2, ["sys_root"]));
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 120,
            minWidth: 60,
        };
    }
    filterToRecordValue(filterValue, comparison, field, userPreferences, recordValue) {
        if (comparison !== "is" || !(0, fields_1.isFieldNested)(field) || !filterValue) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        recordValue = recordValue !== null && recordValue !== void 0 ? recordValue : this.getEmptyValue();
        return {
            ...recordValue,
            [field.slug]: filterValue,
        };
    }
    getContentSize() {
        const iconSize = 27;
        return 4 * iconSize;
    }
    isSupportedByBulkAction() {
        return false;
    }
}
exports.SocialNetworkFieldLogic = SocialNetworkFieldLogic;
